import { BuyMaterial } from "@1games-ru/region-shared/dist/backend/requests/business";
import { DeliveryType } from "@1games-ru/region-shared/dist/enums/business/delivery";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import BusinessMpProvider from "@/core/data-provider/data-mp/business/business-mp-provider";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import { setGoodsAndWarehouseData, setInfo, setPcInfo } from "@/domains/business/slice";
import { playerInfoSelector } from "@/domains/player/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useGetGoodsAndWarehouseData(businessId: number) {
  const { t, i18n } = useTranslation("business");
  const characterInfo = useAppSelector(playerInfoSelector);
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [isPaying, setIsPaying] = useState(false);

  const load = useCallback(
    async (cb?: () => void) => {
      if (businessId) {
        setIsLoading(true);

        try {
          const response = await BusinessMpProvider.getWarehouseGoodsList(businessId);

          if (response instanceof RpcMpProviderSuccessResponse) {
            if (cb) cb();
            dispatch(setGoodsAndWarehouseData(response.data));
          } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("business.toast.goodsAndWarehouseNotLoaded"));
          }
        } catch (e) {
          console.error(e);
          toast.error(t("business.toast.goodsAndWarehouseNotLoaded"));
        } finally {
          setIsLoading(false);
        }
      }
    },
    [businessId, i18n, dispatch, t],
  );

  const loadInfo = useCallback(async () => {
    if (businessId) {
      try {
        const response = await BusinessMpProvider.getInfo(businessId);

        if (response instanceof RpcMpProviderSuccessResponse) {
          dispatch(setPcInfo(response.data));
          dispatch(setInfo(response.data));
        } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
          console.error(response.error.message);
          toast.error(t(`business.errors.${response.error.message}`));
        } else {
          console.error(response.error.message);
          toast.error(t("business.toast.infoLoadError"));
        }
      } catch (e) {
        console.error(e);
        toast.error(t("business.toast.infoLoadError"));
      }
    }
  }, [businessId, i18n, dispatch, t]);

  useEffect(() => {
    load();
  }, [load]);

  const pay = useCallback(
    async (deliveryType: DeliveryType, materials: BuyMaterial[], cb: () => void) => {
      if (businessId && characterInfo?.character?.id && deliveryType && materials) {
        setIsPaying(true);

        try {
          const response = await BusinessMpProvider.payWarehouseGoodsList(
            businessId,
            characterInfo.character.id,
            deliveryType,
            materials,
          );

          if (response instanceof RpcMpProviderSuccessResponse) {
            load(cb);
            loadInfo();
            toast.success(t("business.toast.goodsAndWarehouseBuySuccess"));
          } else if (i18n.exists(`business:business.errors.${response.error.message}`)) {
            console.error(response.error.message);
            toast.error(t(`business.errors.${response.error.message}`));
          } else {
            console.error(response.error.message);
            toast.error(t("business.toast.goodsAndWarehouseBuyError"));
          }
        } catch (e) {
          console.error(e);
          toast.error(t("business.toast.goodsAndWarehouseBuyError"));
        } finally {
          setIsPaying(false);
        }
      }
    },
    [businessId, characterInfo.character.id, i18n, load, loadInfo, t],
  );

  return {
    isLoading,
    pay,
    isPaying,
  };
}
